// The drawer's Owners block: the four people Salesforce names on a row, with which one the board
// shows as its rep (GRO-237). On most renewals the opportunity owner is an inactive user, so the
// rep is the account owner, else the CS owner.

import type { Opportunity, RepSource, SalesforceUser } from '../api/types.ts';

// One of the four, as a line shows it: nobody (null), or a person with whether they're active.
interface OwnerLine {
  label: string;
  person: SalesforceUser | null;
  isRep: boolean;
}

function ownerLines(opportunity: Opportunity): OwnerLine[] {
  const source: RepSource = opportunity.rep.source;
  return [
    { label: 'Opportunity owner', person: opportunity.owner, isRep: source === 'opportunity_owner' },
    { label: 'Account owner', person: opportunity.account_owner, isRep: source === 'account_owner' },
    { label: 'SDR', person: opportunity.sdr_owner, isRep: false },
    { label: 'CS owner', person: opportunity.cs_owner, isRep: source === 'cs_owner' },
  ];
}

function OwnerValue({ line }: { line: OwnerLine }) {
  if (line.person === null) return <span className="muted">—</span>;
  return (
    <>
      <span className="owner-name">{line.person.name}</span>
      {line.isRep ? <span className="owner-tag owner-tag-rep" title="Shown as the rep on the board">Rep</span> : null}
      {line.person.is_active ? null : <span className="owner-tag" title="This Salesforce user is inactive">inactive</span>}
    </>
  );
}

export function OwnersSection({ opportunity }: { opportunity: Opportunity }) {
  return (
    <section>
      <h3>Owners</h3>
      {opportunity.booking_attribution === 'self-booked'
        ? <p>Self-booked · excluded from SDR credit</p>
        : opportunity.booking_attribution === 'unattributed'
          ? <p className="muted">No SDR on the account</p> : null}
      <dl className="owner-list">
        {ownerLines(opportunity).map(line => (
          <div key={line.label} className="owner-line">
            <dt>{line.label}</dt>
            <dd><OwnerValue line={line} /></dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
